// SidebarHost — projects the shell model into the pure Sidebar (pen Q82
// "Search and a minimal left menu", decided 3 Nov).
//
// The composition root of the menu: it registers what works today, on behalf
// of the modules (the Sidebar itself imports none):
//   - menu entries: Editor (the Studio's user-facing name), Projects,
//     Sources, Materials (pen Q82: only what a teacher uses every day; the
//     menu follows the search page's tabs, Sources first). Projects and the
//     Editor are two views (pen "Projects and Studio, two menu items",
//     Decided 30 Oct): Projects is always all projects; the Editor is
//     always the Studio, the open project as left (none open: its empty
//     view). Neither opens or folds the menu: only the author does (owner,
//     30 Oct);
//   - recent items: the projects (useProjectItems.ts) in pen's list pattern
//     (pins, sort and density per device: useRecentList), then "Show all
//     projects"; the open project is marked in every view. No conversations:
//     the chat is a contextual panel, and its conversations live in its
//     picker (pen, Chat panel Decided 01). The registration stays generic,
//     so another module can register a list here;
//   - foot rows: none today (the guided tour and Give feedback live in the
//     Help tab, pen Q68); Events joins when that module exists.
// The school switcher would show the workspace, but only to people in more
// than one: one per account today, so it shows nothing. The foot (pen Q82):
// one "you" row that opens Settings, where Theme, Language, Sign out and
// the version live. The fold button floats
// just outside the menu; in the Studio, whose top bar has its own in that
// place, the menu leaves it out in every layout, so the two never lie over
// the project's title.
//
// The menu's width is the shared `frame.nav` state (toggle-rail), remembered
// per device (railPreference.ts). Collapsed, it is the icon rail plus the peek:
// the full menu laid over the content while the rail is hovered (pen I-13),
// pure CSS. The rail shows the recent projects as letter tiles (pen "Shell ·
// Rail", 29 Oct).
//
// The connection popover (J-05) shows no developer info: its one link,
// "Copy error details", copies the error ID, version and time (SF-14).

export { SidebarHost };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { ConnectionIndicator } from "../../../components/Health/ConnectionIndicator.js";
import { RemoveProjectDialog } from "../../../components/Projects/RemoveProjectDialog.js";
import { tabOfFacet } from "../../../components/SearchPage/searchRows.js";
import {
    Sidebar,
    type SidebarLayout,
} from "../../../components/Sidebar/Sidebar.js";
import {
    type FootAccount,
    SidebarFoot,
} from "../../../components/Sidebar/SidebarFoot.js";
import {
    type SidebarEntry,
    schoolSwitcherOf,
} from "../../../components/Sidebar/sidebarEntries.js";
import { useCopy } from "../../copy.js";
import { externalErrorId } from "../../errorId.js";
import { mkUnreachable } from "../../externalError.js";
import { hasTopBar, studioOpenOf } from "../Centre/studioOpen.js";
import { useAppShell } from "../context.js";
import { accountIdentityOf } from "./accountIdentity.js";
import { useProjectItems } from "./useProjectItems.js";

const SidebarHost = (): ReactNode => {
    const { t } = useLingui();
    const copy = useCopy();
    const {
        tabs,
        frame,
        dispatch,
        kbdPlatform,
        gotoSearch,
        summonSearch,
        gotoSettings,
        authStatus,
        displayName,
        library,
        workspaceName,
        health,
        healthReason,
        nextRetryAt,
        retryNow,
    } = useAppShell();
    const expanded = frame.nav === "expanded";
    const gotoProjects = () => dispatch({ type: "open-projects" });
    const active = tabs.active;
    const project = useProjectItems();

    const menu: readonly SidebarEntry[] = [
        {
            type: "menu-entry",
            id: "studio",
            label: t`Editor`,
            icon: "square-pen",
            works: true,
            current: active.type === "studio",
            open: () => dispatch({ type: "open-studio" }),
        },
        {
            type: "menu-entry",
            id: "projects",
            label: t`Projects`,
            icon: "layout-grid",
            works: true,
            current: active.type === "projects",
            open: gotoProjects,
        },
        {
            type: "menu-entry",
            id: "knowledge",
            label: t`Sources`,
            icon: "file-up",
            works: true,
            current:
                (active.type === "search" &&
                    tabOfFacet(active.kind) === "sources") ||
                active.type === "knowledge",
            open: () => gotoSearch("source"),
        },
        {
            type: "menu-entry",
            id: "library",
            label: t`Materials`,
            icon: "library",
            works: true,
            current:
                active.type === "search" &&
                tabOfFacet(active.kind) === "materials",
            open: () => gotoSearch("material"),
        },
    ];

    const projects: readonly SidebarEntry[] = project.items;

    // Not known until the auth probe answers; signed out (a local stack
    // without sign-in) is the default case, "Account".
    const account: FootAccount =
        authStatus === null
            ? { type: "loading" }
            : {
                  type: "known",
                  identity: accountIdentityOf(
                      displayName,
                      authStatus._tag === "authenticated"
                          ? authStatus.user.email
                          : "",
                  ),
              };

    // The workspace stands in for the institution until institutions exist;
    // shown once the library has loaded (its name comes with it).
    const school =
        library === null
            ? schoolSwitcherOf([], "", undefined)
            : schoolSwitcherOf(
                  [
                      {
                          id: "workspace",
                          name:
                              workspaceName === ""
                                  ? t`Unnamed workspace`
                                  : workspaceName,
                      },
                  ],
                  "workspace",
                  undefined,
              );

    // The connection trouble's reference: the server could not be reached.
    const copyConnectionDetails =
        copy === null
            ? undefined
            : () =>
                  copy({
                      type: "error-info",
                      errorId: externalErrorId(mkUnreachable()),
                  });

    const toggle = () => dispatch({ type: "toggle-rail" });
    // The Studio's top bar has the menu's toggle (a project open, or still
    // loading); the empty Studio has no bar, so the menu keeps its own.
    const studioBar =
        active.type === "studio" &&
        hasTopBar(
            studioOpenOf(
                frame.studioProject,
                library === null ? null : (library.projects ?? []),
            ),
        );
    const sidebarIn = (layout: SidebarLayout): ReactNode => {
        const labelled = layout !== "rail";
        return (
            <Sidebar
                layout={layout}
                onToggle={studioBar ? null : toggle}
                school={school}
                onNewProject={project.newProject}
                onOpenSearch={summonSearch}
                platform={kbdPlatform}
                entries={[...menu, ...projects]}
                recentLists={[
                    {
                        id: "projects",
                        view: project.view,
                        onCommand: project.onCommand,
                        labels: {
                            pinned: t`Pinned projects`,
                            recent: t`Recent projects`,
                        },
                        showAll: {
                            label: t`Show all projects`,
                            open: gotoProjects,
                        },
                        loading: false,
                    },
                ]}
                foot={
                    <SidebarFoot
                        expanded={labelled}
                        settingsCurrent={active.type === "settings"}
                        onSettings={gotoSettings}
                        account={account}
                    />
                }
                connection={
                    <ConnectionIndicator
                        health={health}
                        reason={healthReason}
                        nextRetryAt={nextRetryAt}
                        variant={labelled ? "pill" : "icon"}
                        onRetryNow={retryNow}
                        onCopyDetails={copyConnectionDetails}
                    />
                }
            />
        );
    };

    // "Delete …" on a project in the menu asks first, here.
    const removal = <RemoveProjectDialog {...project.removal} />;
    return expanded ? (
        <>
            {sidebarIn("expanded")}
            {removal}
        </>
    ) : (
        <div className="bilda-sidebar-host">
            {sidebarIn("rail")}
            <div className="bilda-sidebar-peek">{sidebarIn("peek")}</div>
            {removal}
        </div>
    );
};
